import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail } from 'lucide-react';

const API = (typeof window !== 'undefined' ? window.location.origin : '');

/**
 * Gift delivery step — one email capture, then the Auto Flow status screens.
 * The voucher code and the sponsor app code NEVER appear here: both arrive by
 * email (the voucher only behind its token-gated download link).
 */
export const GiftEmailDelivery = ({ sessionId, path, prefillEmail, merchantName, onDone }) => {
    const { t, i18n } = useTranslation();
    const isApp = path === 'app';
    const [email, setEmail] = useState(prefillEmail || '');
    const [accepted, setAccepted] = useState(false);
    const [stage, setStage] = useState('form');   // form | registering | done
    const [error, setError] = useState(null);
    const [showTerms, setShowTerms] = useState(false);

    const submit = async () => {
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
            setError(t('loyalReview.email.invalid')); return;
        }
        if (!accepted) { setError(t('loyalReview.terms.mustAccept')); return; }
        setError(null);
        setStage('registering');
        try {
            const res = await fetch(`${API}/api/vouchers/gift/deliver/${sessionId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim(), path: isApp ? 'app' : 'no_app',
                    tc_accepted: true, tc_language: (i18n.language || 'en').split('-')[0],
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !(data.delivered || data.already_delivered)) throw new Error('deliver_failed');
            setTimeout(() => { setStage('done'); onDone?.(data); }, 1600);
        } catch {
            setError(t('giftGenerator.deliverFailed'));
            setStage('form');
        }
    };

    if (stage !== 'form') {
        return (
            <div className="w-full max-w-sm mx-auto text-center" data-testid="gift-delivery-status">
                <div
                    key={stage}
                    className={`mt-4 rounded-xl px-4 py-4 bg-slate-900/50 border border-white/10 flex flex-col items-center justify-center text-center ${stage === 'registering' ? 'animate-status-pulse' : ''}`}
                    role="status" aria-live="polite"
                >
                    <p className="text-white font-bold text-base uppercase tracking-wide leading-tight"
                        data-testid={stage === 'registering' ? 'gift-delivery-stage1' : 'gift-delivery-stage2'}>
                        {stage === 'registering'
                            ? t('loyalReview.autoFlow.registeringGift', 'Registering your gift')
                            : t('loyalReview.autoFlow.congrats', 'Congratulations!')}
                    </p>
                </div>
                <p className="text-white text-sm mt-3 leading-snug" data-testid="gift-delivery-subline">
                    {stage === 'registering'
                        ? t('giftGenerator.sending')
                        : (isApp
                            ? t('giftGenerator.appCodeOnItsWay')
                            : t('loyalReview.autoFlow.giftOnItsWay', 'Your gift card is on its way!'))}
                </p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-sm mx-auto" data-testid="gift-delivery-form">
            <h2 className="text-2xl font-bold text-white text-center mb-2">
                {t('giftGenerator.emailHeading')}
            </h2>
            <p className="text-slate-300 text-center text-sm mb-5">
                {isApp ? t('giftGenerator.emailBodyApp') : t('giftGenerator.emailBodyVoucher')}
            </p>
            <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    type="email" inputMode="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('loyalReview.email.placeholder')}
                    className="w-full rounded-xl bg-slate-900/70 border border-white/15 text-white pl-9 pr-3 py-3 text-base outline-none focus:border-white/40"
                    data-testid="gift-delivery-email-input"
                />
            </div>
            <label className="flex items-start gap-2 mt-4 text-left">
                <input
                    type="checkbox" checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-500"
                    data-testid="gift-delivery-terms-checkbox"
                />
                <span className="text-xs text-slate-300">
                    {t('loyalReview.terms.accept')}{' '}
                    <button type="button" className="underline text-slate-200"
                        onClick={() => setShowTerms((v) => !v)} data-testid="gift-delivery-terms-link">
                        {t('loyalReview.terms.viewLink', 'View Terms & Conditions')}
                    </button>
                </span>
            </label>
            {showTerms && (
                <p className="mt-3 text-[11px] leading-relaxed text-slate-400 max-h-40 overflow-y-auto"
                    data-testid="gift-delivery-terms-body">
                    {t('loyalReview.terms.body', {
                        merchant: merchantName || t('loyalReview.terms.merchantFallback', 'the merchant'),
                    })}
                </p>
            )}
            {error && (
                <p className="mt-3 text-sm text-red-400 text-center" data-testid="gift-delivery-error">{error}</p>
            )}
            <button
                type="button" onClick={submit}
                className="mt-5 w-full rounded-xl bg-white text-slate-900 font-bold py-3.5 text-base active:scale-[0.99] transition-transform"
                data-testid="gift-delivery-submit"
            >
                {t('loyalReview.email.continue')}
            </button>
        </div>
    );
};
